import { escapeHtml } from '../format.js';
import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent, MenuSection } from '../types.js';
import {
  badge,
  bilingual,
  description,
  hasTitle,
  image,
  listItemHeight,
  ARABIC_LINE,
  price,
  sectionTitle,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * Pizza Showcase, "Size table": the black-and-red pizzeria look as a price table. Two columns of
 * rows, each a small round photo, the name in yellow and a price under each size heading (Medium,
 * Large), with Italian stripes under the section titles. Single-price items span the size columns.
 */

const S = '.mb-t-pizza-show-hero';

/** Green, white and red bars: the Italian stripe under the brand. */
const STRIPE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 3 1' preserveAspectRatio='none'%3E%3Crect width='1' height='1' fill='%23169b4c'/%3E%3Crect x='1' width='1' height='1' fill='%23f4f4f4'/%3E%3Crect x='2' width='1' height='1' fill='%23d52b1e'/%3E%3C/svg%3E\")";
/** A sprig of basil beside the brand. */
const BASIL =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Cpath d='M30 62C29 48 30 34 36 20' stroke='%232f6f2a' stroke-width='2.4' fill='none' stroke-linecap='round'/%3E%3Cpath d='M35 22C22 22 11 13 10 3C24 2 35 10 35 22Z' fill='%233f9b35'/%3E%3Cpath d='M35 22C26 15 19 9 10 3' stroke='%23296324' stroke-width='1.2' fill='none'/%3E%3Cpath d='M36 24C46 15 58 15 63 22C55 33 43 33 36 24Z' fill='%234fb043'/%3E%3Cpath d='M36 24C45 22 54 21 63 22' stroke='%232f7a2a' stroke-width='1.2' fill='none'/%3E%3Cpath d='M31 40C20 44 9 39 5 29C17 25 27 30 31 40Z' fill='%23388d30'/%3E%3Cpath d='M31 40C22 36 13 32 5 29' stroke='%23245c20' stroke-width='1.2' fill='none'/%3E%3Cpath d='M32 44C40 37 51 38 56 45C49 53 38 52 32 44Z' fill='%2345a33b'/%3E%3C/svg%3E\")";

/** The size columns of a block: every size label its items use, in first-seen order, up to three. */
function sizeLabels(items: MenuItemContent[]): { label: string; labelAr: string | null }[] {
  const seen = new Map<string, string | null>();
  for (const item of items) for (const v of item.variants) if (!seen.has(v.label)) seen.set(v.label, v.labelAr);
  return [...seen.entries()].slice(0, 3).map(([label, labelAr]) => ({ label, labelAr }));
}

const THUMB = 4.6;

function column(board: MenuBoardContent, portrait: boolean): MenuColumn {
  const both = board.settings.language === 'both';
  const item = { nameEm: 1.45, descEm: 0.85, perLine: portrait ? 50 : 40, gap: 1.2 };
  const heading = (section: MenuSection) =>
    hasTitle(section) ? 4.4 * (both && section.titleAr ? 1.45 : 1) : 2.2;
  // On a bilingual board the Arabic name shares the English name's line, at the far end.
  const rowText = (i: MenuItemContent) =>
    listItemHeight(board, i, item) - (both && i.titleAr ? item.nameEm * 0.85 * 1.2 * ARABIC_LINE : 0);
  return {
    capacity: portrait ? 100 : 54,
    blockCost: (section, items) =>
      heading(section) + items.reduce((sum, i) => sum + Math.max(THUMB + 1.2, rowText(i)), 0) + 1.6,
  };
}

function cells(ctx: RenderContext, item: MenuItemContent, sizes: { label: string }[]): string {
  if (item.variants.length && sizes.length) {
    return sizes
      .map((s) => {
        const v = item.variants.find((x) => x.label === s.label);
        return `<span class="psh-cell" dir="ltr">${v ? price(ctx, v.price) : '—'}</span>`;
      })
      .join('');
  }
  const cheapest = item.variants.length ? Math.min(...item.variants.map((x) => x.price)) : null;
  const old =
    cheapest === null && item.discountPrice !== null && item.discountPrice < item.price
      ? `<s class="psh-old" dir="ltr">${price(ctx, item.price)}</s>`
      : '';
  const now = cheapest ?? (old ? item.discountPrice! : item.price);
  return `<span class="psh-cell psh-span" style="--span:${Math.max(1, sizes.length)}"><span dir="ltr">${price(ctx, now)}</span>${old}</span>`;
}

function row(ctx: RenderContext, item: MenuItemContent, sizes: { label: string }[]): string {
  const photo = image(ctx, item.imageMediaId, 'psh-img');
  const initial = escapeHtml((item.title.trim()[0] ?? '?').toUpperCase());
  const badges = badge(ctx, item, 'psh-badge');
  return [
    '<div class="psh-row">',
    `<span class="psh-thumb">${photo || `<span class="psh-initial">${initial}</span>`}</span>`,
    '<div class="psh-txt">',
    `<div class="psh-nline">${bilingual(ctx, item.title, item.titleAr, 'psh-name')}${badges ? `<span class="psh-badges">${badges}</span>` : ''}</div>`,
    description(ctx, item, 'psh-desc'),
    '</div>',
    `<div class="psh-cells" style="--n:${Math.max(1, sizes.length)}">${cells(ctx, item, sizes)}</div>`,
    '</div>',
  ].join('');
}

function block(ctx: RenderContext, b: MenuBlock): string {
  const sizes = sizeLabels(b.items);
  const heads = sizes
    .map((s) => `<span class="psh-size">${escapeHtml(ctx.showEn ? s.label : s.labelAr || s.label)}</span>`)
    .join('');
  const title = hasTitle(b.section)
    ? `<div class="psh-stitles">${sectionTitle(ctx, b.section, 'psh-stitle')}<span class="psh-sstripe"></span></div>`
    : '<div class="psh-stitles"></div>';
  return [
    '<div class="psh-sec">',
    `<div class="psh-shead">${title}<div class="psh-sizes" style="--n:${Math.max(1, sizes.length)}">${heads}</div></div>`,
    b.items.map((i) => row(ctx, i, sizes)).join(''),
    '</div>',
  ].join('');
}

function brand(ctx: RenderContext): string {
  const { board } = ctx;
  const has = board.brand.name?.trim() || board.brand.nameAr?.trim();
  const text = has
    ? bilingual(ctx, board.brand.name, board.brand.nameAr, 'psh-bname')
    : bilingual(ctx, board.settings.title ?? board.name, board.settings.titleAr, 'psh-bname');
  return `<div class="psh-brand"><div class="psh-bwords">${text}<span class="psh-stripe"></span></div><span class="psh-basil"></span></div>`;
}

export const pizzaShowHero: MenuTemplate = {
  id: 'pizza-show-hero',
  name: 'Pizza showcase: size table',
  description:
    'Rows with small round photos and a price under each size heading (Medium, Large), in two columns on black with Italian stripes. Made for pizzas, drinks and anything sold by size.',
  defaultTheme: {
    backgroundColor: '#0b0b0b',
    surfaceColor: '#1b1b1b',
    textColor: '#ffffff',
    primaryColor: '#ffc72c',
    accentColor: '#d62828',
    fontId: 'bold',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'black-red', name: 'Black & red', theme: {} },
    { id: 'charcoal-green', name: 'Charcoal & green', theme: { backgroundColor: '#1d2023', surfaceColor: '#292d31', textColor: '#f3f3f1', primaryColor: '#ffd166', accentColor: '#1f7a4d' } },
    { id: 'navy-orange', name: 'Navy & orange', theme: { backgroundColor: '#0d1828', surfaceColor: '#16253b', textColor: '#f4f6fa', primaryColor: '#ffd23f', accentColor: '#e8590c' } },
    { id: 'cream-red', name: 'Cream paper & red', theme: { backgroundColor: '#f5ecdc', surfaceColor: '#fffaf0', textColor: '#2a211b', primaryColor: '#9e1b14', accentColor: '#c8102e' } },
  ],
  cuisines: ['pizza', 'drinks'],
  family: { id: 'pizza-show', name: 'Pizza Showcase', layout: 'Size table' },
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    const portrait = orientation === 'portrait';
    const col = column(board, portrait);
    return packColumns(groupBySection(board), portrait ? [col] : [col, col], { balance: !portrait });
  },

  render(ctx) {
    const { board } = ctx;
    const title = bilingual(ctx, board.settings.title, board.settings.titleAr, 'psh-title');
    const header = `<div class="psh-head">${brand(ctx)}${title ? `<div class="psh-tag">${title}</div>` : ''}</div>`;
    const blocks = ctx.page.columns.flat();
    const body = blocks.length
      ? `<div class="psh-cols">${ctx.page.columns
          .map((c) => `<div class="psh-col">${c.map((b) => block(ctx, b)).join('')}</div>`)
          .join('')}</div>`
      : '<div class="mb-empty">No items on this board yet</div>';
    return header + body;
  },

  css: `
${S}{padding:2.4em 3.2em 2.4em;background-image:radial-gradient(ellipse at 50% 35%,rgba(255,255,255,.05),transparent 65%)}
${S} .psh-head{display:flex;align-items:flex-start;justify-content:space-between;gap:2em;margin-bottom:1.8em}
${S} .psh-brand{display:flex;align-items:flex-start;gap:.6em}
${S} .psh-bwords{display:flex;flex-direction:column;align-items:stretch}
${S} .psh-bname{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:3.4em;line-height:1;
  text-transform:uppercase;letter-spacing:.06em;color:var(--mb-text)}
${S} .psh-bname.mb-2nd{font-size:2em;letter-spacing:0;margin-top:.1em}
${S} .psh-stripe{display:block;height:.42em;margin-top:.5em;width:100%;min-width:10em;background:${STRIPE} 0 0/100% 100% no-repeat;border-radius:.1em}
${S} .psh-basil{width:4.6em;height:4.6em;flex-shrink:0;margin-top:-.8em;background:${BASIL} center/contain no-repeat;
  filter:drop-shadow(0 .3em .5em rgba(0,0,0,.4))}
${S} .psh-tag{background:var(--mb-accent);color:#fff;padding:.7em 2.2em .7em 3em;clip-path:polygon(1.6em 0,100% 0,100% 100%,0 100%);
  margin-inline-end:-3.2em;text-align:end;box-shadow:0 .6em 1.2em rgba(0,0,0,.3)}
${S}[dir=rtl] .psh-tag{clip-path:polygon(0 0,calc(100% - 1.6em) 0,100% 100%,0 100%);padding:.7em 3em .7em 2.2em}
${S} .psh-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2.6em;line-height:1.05;
  text-transform:uppercase;letter-spacing:.06em}
${S} .psh-title.mb-2nd{font-size:1.6em;letter-spacing:0}

${S} .psh-cols{flex:1;min-height:0;display:grid;grid-template-columns:repeat(auto-fit,minmax(0,1fr));gap:4em}
${S} .psh-col{min-width:0;display:flex;flex-direction:column;gap:1.6em}
${S} .psh-shead{display:flex;align-items:flex-end;justify-content:space-between;gap:1em;padding-bottom:.5em;
  border-bottom:.14em solid var(--mb-accent);margin-bottom:.2em}
${S} .psh-stitles{min-width:0}
${S} .psh-stitle{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2.4em;line-height:1.05;
  text-transform:uppercase;letter-spacing:.05em;color:var(--mb-primary)}
${S} .psh-stitle.mb-2nd{font-size:1.5em;letter-spacing:0}
${S} .psh-sstripe{display:block;width:6em;height:.3em;margin-top:.35em;background:${STRIPE} 0 0/100% 100% no-repeat;border-radius:.1em}
${S} .psh-sizes{display:grid;grid-template-columns:repeat(var(--n),7em);flex-shrink:0}
${S} .psh-size{text-align:center;font-weight:800;font-size:.95em;text-transform:uppercase;letter-spacing:.08em;white-space:nowrap;
  overflow:hidden;text-overflow:ellipsis}
${S} .psh-row{display:flex;align-items:center;gap:1.1em;padding:.6em 0;border-bottom:.08em solid color-mix(in srgb,var(--mb-text) 18%,transparent)}
${S} .psh-row:last-child{border-bottom:0}
${S} .psh-thumb{position:relative;width:${THUMB}em;height:${THUMB}em;flex-shrink:0;border-radius:50%;overflow:hidden;background:var(--mb-surface);
  box-shadow:0 0 0 .18em color-mix(in srgb,var(--mb-text) 85%,transparent),0 .5em 1em rgba(0,0,0,.4)}
${S} .psh-img{width:100%;height:100%;object-fit:cover;display:block}
${S} .psh-initial{display:flex;width:100%;height:100%;align-items:center;justify-content:center;font-size:2em;opacity:.35;
  font-family:var(--mb-font-heading)}
${S} .psh-txt{flex:1;min-width:0}
${S} .psh-nline{display:flex;align-items:center;flex-wrap:wrap;gap:0 .6em}
${S} .psh-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.45em;line-height:1.15;
  text-transform:uppercase;letter-spacing:.04em;color:var(--mb-primary);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .psh-name.mb-2nd{margin-left:auto;font-size:1.2em;letter-spacing:0;color:var(--mb-text);opacity:.9}
${S} .psh-badges{display:flex;gap:.3em}
${S} .psh-badge{background:var(--mb-accent);color:#fff;font-weight:800;font-size:.75em;padding:.15em .55em;border-radius:99em;white-space:nowrap}
${S} .psh-desc{font-size:.85em;opacity:.7;margin-top:.15em;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .psh-cells{display:grid;grid-template-columns:repeat(var(--n),7em);flex-shrink:0}
${S} .psh-cell{text-align:center;font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.45em;
  white-space:nowrap;letter-spacing:.03em}
${S} .psh-cells .psh-cell{font-size:1.45em}
${S} .psh-span{grid-column:1/-1;display:flex;flex-direction:column;align-items:center;line-height:1.05}
${S} .psh-old{font-family:var(--mb-font-body);font-size:.55em;opacity:.55}
`,
};
